import { memo, ReactNode } from 'react';
import { METRICS_CONFIG } from '../config';
import { useIsMobile } from '../../../hooks/useIsMobile';

export type MetricCardProps = {
  value: string | number;
  label: ReactNode;
  accent: string;
  className?: string;
  secondaryValue?: string | number;
  secondaryLabel?: ReactNode;
  preserveSecondarySpacing?: boolean;
  helpTooltip?: ReactNode;
};

function MetricCardBase({
  value,
  label,
  accent,
  className = '',
  secondaryValue,
  secondaryLabel,
  preserveSecondarySpacing = false,
  helpTooltip,
}: MetricCardProps) {
  const isMobile = useIsMobile();
  const showSecondary = secondaryValue !== undefined && secondaryValue !== null;
  const shouldRenderSecondary = preserveSecondarySpacing || showSecondary;

  return (
    <div
      className={`relative rounded-lg border border-[var(--anl-border)] bg-[var(--anl-metric-bg)] ${className}`}
    >
      {helpTooltip ? <div className="absolute right-2 top-2">{helpTooltip}</div> : null}

      <div className="p-3 pb-4">
        <div className="mb-2 pr-7 text-xs font-medium text-[var(--anl-text-muted)]">{label}</div>
        <div className="flex items-end justify-between gap-4">
          <div className={`flex min-h-[2.25rem] min-w-0 flex-col justify-end font-bold leading-none text-[var(--anl-text)] ${isMobile ? 'text-base' : 'text-xl'}`}>
            <span>{value}</span>
          </div>
          {shouldRenderSecondary && (
            <div
              className={`flex flex-col items-end justify-end text-right ${isMobile ? 'min-w-[4rem]' : 'min-w-[6.5rem]'} ${showSecondary ? '' : 'invisible'}`}
              aria-hidden={showSecondary ? undefined : true}
            >
              {secondaryLabel !== undefined && (
                <div className="mb-1 text-xs leading-none text-[var(--anl-text-muted)]">
                  {secondaryLabel}
                </div>
              )}
              <div className={`font-semibold leading-none text-[var(--anl-text)] ${isMobile ? 'text-sm' : 'text-lg'}`}>
                {showSecondary ? secondaryValue : METRICS_CONFIG.DEFAULT_STRING}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 rounded-b-lg"
        style={{ height: 6, backgroundColor: accent }}
      />
    </div>
  );
}

export const MetricCard = memo(MetricCardBase);
